import { useCallback, useEffect, useRef, useState } from "react"
import * as signalR from "@microsoft/signalr"
import { SHUK_AUTH_PORTAL_TOKEN } from "redux/enums"

// Full hub url = <REACT_APP_BASE_SIGNALR_CON>notificationHub
// e.g. http://25.11.249.196:5284/notificationHub
const HUB_URL = `${process.env.REACT_APP_BASE_SIGNALR_CON || ""}hubs/notifications`

// Client method names the backend hub may invoke on connected clients.
// If the hub uses a different method name, add/adjust it here.
export const NOTIFICATION_EVENTS = [
  "ReceiveNotification",
  "ReceiveMessage",
  "Notification",
  "SendNotification",
  "newNotification",
]

/**
 * Opens a SignalR connection to the notification hub and surfaces the
 * notifications it pushes. The connection is opened on mount and closed on
 * unmount, and automatically reconnects if the socket drops.
 *
 * @param {(payload:any) => void} onNotification - optional callback fired for
 *   every incoming notification (e.g. to refresh a list).
 * @returns {{ connected: boolean, notifications: any[], clearNotifications: () => void }}
 */
export default function useNotificationHub(onNotification) {
  const [connected, setConnected] = useState(false)
  const [notifications, setNotifications] = useState([])
  const connectionRef = useRef(null)
  const callbackRef = useRef(onNotification)

  // Keep the latest callback without re-opening the connection.
  useEffect(() => {
    callbackRef.current = onNotification
  }, [onNotification])

  useEffect(() => {
    if (!process.env.REACT_APP_BASE_SIGNALR_CON) {
      console.log("SignalR: REACT_APP_BASE_SIGNALR_CON is not set, skipping.")
      return undefined
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        // Negotiation is used (default) so the client can pick a transport
        // (WebSockets, then SSE / long-polling fallback). The server returns
        // Access-Control-Allow-Origin: * which the browser rejects for
        // credentialed requests, so connect without credentials (cookies).
        // The JWT is still sent (Authorization header on negotiate, and as the
        // access_token query param on the WebSocket).
        withCredentials: false,
        accessTokenFactory: () =>
          localStorage.getItem(SHUK_AUTH_PORTAL_TOKEN) || "",
      })
      // Reconnect forever (default policy gives up after ~30s). Back off up to
      // 30s between attempts so a long internet outage keeps retrying.
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext =>
          Math.min(30000, 2000 * (retryContext.previousRetryCount + 1)),
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build()

    connectionRef.current = connection
    console.log("signal connection", connection)

    const handleNotification = (...args) => {
      // Hubs may send a single payload or multiple arguments.
      const payload = args.length > 1 ? args : args[0]
      console.log("SignalR notification received:", payload)
      setNotifications(prev =>
        [
          {
            id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            payload,
            receivedAt: new Date().toISOString(),
          },
          ...prev,
        ].slice(0, 20),
      )
      if (callbackRef.current) callbackRef.current(payload)
    }

    NOTIFICATION_EVENTS.forEach(evt => connection.on(evt, handleNotification))

    let isMounted = true
    let stopped = false
    let isStarting = false
    let wasOffline = false

    // Start (or re-start) the connection. Guards against concurrent/duplicate
    // starts and keeps retrying until it succeeds.
    const startConnection = async () => {
      if (!isMounted || stopped || isStarting) return
      // Only start from a fully-disconnected state; auto-reconnect handles the
      // Connecting/Reconnecting states on its own.
      if (connection.state !== signalR.HubConnectionState.Disconnected) return
      isStarting = true
      try {
        await connection.start()
        if (isMounted) setConnected(true)
        console.log("SignalR notificationHub connected:", HUB_URL)
      } catch (err) {
        console.log("SignalR connection error:", err)
        if (isMounted && !stopped) setTimeout(startConnection, 5000)
      } finally {
        isStarting = false
      }
    }

    connection.onreconnecting(() => {
      if (isMounted) setConnected(false)
    })
    connection.onreconnected(() => {
      if (isMounted) setConnected(true)
    })
    connection.onclose(() => {
      if (isMounted) setConnected(false)
      // Auto-reconnect has given up (or the socket closed before it engaged) —
      // keep trying so it recovers from any-length outage.
      if (isMounted && !stopped) setTimeout(startConnection, 5000)
    })

    const handleOffline = () => {
      wasOffline = true
      if (isMounted) setConnected(false)
    }
    // When the browser regains network after being offline, SignalR may still
    // believe it is Connected (stale socket) or be stuck in a long reconnect
    // backoff, so a plain start() is a no-op. Force a clean teardown and a
    // fresh connection to guarantee recovery.
    const handleOnline = async () => {
      if (!isMounted || !wasOffline) return
      wasOffline = false
      console.log("Network back online — forcing SignalR reconnect")
      try {
        await connection.stop()
      } catch (e) {
        // ignore
      }
      startConnection()
    }
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    startConnection()

    return () => {
      isMounted = false
      stopped = true
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
      NOTIFICATION_EVENTS.forEach(evt =>
        connection.off(evt, handleNotification),
      )
      connection.stop().catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clearNotifications = useCallback(() => setNotifications([]), [])

  return { connected, notifications, clearNotifications }
}
